import CircularProgress from '@mui/material/CircularProgress'
import classnames from 'classnames'
import moment from 'moment'
import React from 'react'
import { CartesianGrid, Label, Legend, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from 'recharts'
import LegendComponent from '../../common/legend/Legend'
import type { OptionType } from '../../common/select/Select'
import Select from '../../common/select/Select'
import { i18n } from '../index'
import css from './Reporting.css'
type Props = {
  month: string
}
type DailyData = {
  actual: number | null
  estimate: number | null
}
type SalesData = {
  date: string
  buffet: DailyData
  salty: DailyData
  sweet: DailyData
}
type State = {
  data: Array<SalesData>
  loading: boolean
  selectedProduct: string
}
const PRODUCTS = ['buffet', 'salty', 'sweet']
const MONTH_FORMAT = 'MM-YYYY'
const DAY_FORMAT = 'DD-MM-YYYY'

const generateDailyData: (arg0: boolean) => DailyData = (isZero) => {
  if (isZero) {
    return {
      actual: null,
      estimate: null,
    }
  } else {
    return {
      actual: Math.floor(Math.random() * 80 + 75),
      estimate: Math.floor(Math.random() * 80 + 75),
    }
  }
}

type ComparisonDataProps = {
  product: DailyData
}

type GraphData = {
  date: string
  buffet: DailyData
  salty: DailyData
  sweet: DailyData
}

const ComparisonData = ({ product }: ComparisonDataProps) => {
  return product.estimate === null ? (
    <React.Fragment>
      <td className={css.nullValue}>-</td>
      <td className={css.nullValue}>{product.actual ? product.actual : '-'}</td>
    </React.Fragment>
  ) : (
    <React.Fragment>
      <td>{product.estimate}</td>
      <td
        className={classnames(css.overTarget, {
          [css.underTarget]: product.estimate > (product.actual ?? 0),
        })}
      >
        {product.actual}
      </td>
    </React.Fragment>
  )
}

type GraphProps = {
  data: Array<any>
  selectedProduct: string
}

const Graph = ({ selectedProduct, data }: GraphProps) => (
  <div>
    <h4>{i18n.t(`reporting.${selectedProduct}`)}</h4>
    <ResponsiveContainer width='100%' height={300}>
      <LineChart
        margin={{
          top: 30,
          right: 10,
          bottom: 10,
          left: 10,
        }}
        data={data}
        className={css.chart}
      >
        <CartesianGrid />

        <XAxis
          dataKey={'date'}
          interval={0}
          tickFormatter={(item) => moment(item, DAY_FORMAT).format('D')}
          axisLine={{
            stroke: '#ccc',
            strokeWidth: 1,
          }}
          tickSize={0}
          tickMargin={10}
          className={css.green}
        />
        <YAxis
          padding={{
            top: 0,
          }}
          axisLine={{
            stroke: '#ccc',
            strokeWidth: 1,
          }}
          tickCount={6}
          tickSize={0}
          tickMargin={10}
        >
          <Label value={'kpl'} position='top' offset={15} />
        </YAxis>

        <Legend content={<LegendComponent />} />

        <Line
          type='linear'
          dataKey={`${selectedProduct}.estimate`}
          dot={false}
          unit='€'
          name={i18n.t('reporting.estimate')}
          legendType='line'
          strokeWidth={3}
          stroke={STROKES.BUDGET}
        />

        <Line
          type='linear'
          dataKey={`${selectedProduct}.actual`}
          dot={false}
          unit='€'
          name={i18n.t('reporting.actual')}
          legendType='line'
          strokeWidth={3}
          stroke={STROKES.ACTUAL}
        />
      </LineChart>
    </ResponsiveContainer>
  </div>
)

const generateData: (arg0: string) => Array<SalesData> = (month) => {
  const numOfDays = moment(month, MONTH_FORMAT).daysInMonth()
  const data: GraphData[] = []

  for (let i = 0; i < numOfDays; i++) {
    const isZero = Math.random() < 0.1
    data.push({
      date: moment(month, MONTH_FORMAT).add(i, 'days').format(DAY_FORMAT),
      buffet: generateDailyData(isZero),
      salty: generateDailyData(isZero),
      sweet: generateDailyData(isZero),
    })
  }

  return data
}

const STROKES = {
  ACTUAL: '#78A22F',
  BUDGET: '#F7AC01',
}

class CustomerAmounts extends React.Component<Props, State> {
  state = {
    data: [] as Array<SalesData>,
    loading: true,
    selectedProduct: PRODUCTS[0],
  }

  componentDidMount() {
    this.setState({
      loading: false,
      data: generateData(this.props.month),
    })
  }

  selectedProductChanged: (arg0: OptionType) => void = (option) => {
    this.setState({
      selectedProduct: option.value,
    })
  }

  render() {
    const { data, loading, selectedProduct } = this.state
    return (
      <div>
        <h4>{i18n.t('reporting.customerAmounts')}</h4>

        <Select
          className={css.typeSelect}
          options={PRODUCTS}
          getOptionLabel={(option) => i18n.t(`reporting.${option.value}`)}
          getOptionValue={(option) => option}
          value={selectedProduct}
          onChange={this.selectedProductChanged}
        />

        <table className={classnames(css.table, css.narrowTable)}>
          <thead>
            <tr className={css.firstHeader}>
              <th></th>
              <th colSpan={2}>{i18n.t('reporting.buffet')}</th>
              <th colSpan={2}>{i18n.t('reporting.salty')}</th>
              <th colSpan={2}>{i18n.t('reporting.sweet')}</th>
            </tr>
            <tr className={css.secondHeader}>
              <th className={css.firstHeader}>{i18n.t('reporting.date')}</th>
              <th>{i18n.t('reporting.estimate')}</th>
              <th>{i18n.t('reporting.actual')}</th>
              <th>{i18n.t('reporting.estimate')}</th>
              <th>{i18n.t('reporting.actual')}</th>
              <th>{i18n.t('reporting.estimate')}</th>
              <th>{i18n.t('reporting.actual')}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((day) => (
              <tr key={day.date} className={css.tableRow}>
                <td>{moment(day.date, DAY_FORMAT).format('D.M.YYYY')}</td>
                <ComparisonData product={day.buffet} />
                <ComparisonData product={day.salty} />
                <ComparisonData product={day.sweet} />
              </tr>
            ))}
          </tbody>
        </table>

        {loading ? (
          <CircularProgress />
        ) : (
          <React.Fragment>
            <Graph data={data} selectedProduct='buffet' />
            <Graph data={data} selectedProduct='salty' />
            <Graph data={data} selectedProduct='sweet' />
          </React.Fragment>
        )}
      </div>
    )
  }
}

export default CustomerAmounts
